import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, MapPin, Quote } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const EASE = [0.16, 1, 0.3, 1];

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(undefined);

  // Hooks must run on every render, so this sits above the early returns for
  // the loading and not-found states.
  useSEO({
    title: project?.title,
    path: `/projects/${slug}`,
    description: project
      ? `${project.title} — ${project.division} project by GENOUM GROUP${project.location ? ` in ${project.location}` : ""}.`
      : undefined,
  });

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    setProject(undefined);
    api.get(`/projects/${slug}`).then((r) => setProject(r.data)).catch(() => setProject(null));
  }, [slug]);

  if (project === undefined) {
    return (
      <div className="bg-navy min-h-screen flex items-center justify-center" data-testid="project-loading">
        <div className="w-6 h-6 border-2 border-electric border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="bg-navy min-h-screen text-slate-50" data-testid="project-not-found">
        <Navbar />
        <main className="pt-40 pb-28 max-w-7xl mx-auto px-6 lg:px-10">
          <h1 className="font-display text-4xl font-black uppercase">Project not found</h1>
          <Link to="/projects" className="font-mono-accent text-xs tracking-[0.2em] uppercase text-neon mt-6 inline-flex items-center gap-2" data-testid="not-found-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Projects
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-navy min-h-screen text-slate-50" data-testid="project-detail-page">
      <Navbar />
      <main>
        <section className="relative min-h-[60vh] flex items-end overflow-hidden">
          <motion.img
            src={project.image}
            alt={project.title}
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.6, ease: EASE }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-navy/20" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full pb-16 pt-40">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <Link
                to="/projects"
                className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-slate-300 hover:text-neon transition-colors duration-300 inline-flex items-center gap-2 mb-8"
                data-testid="back-to-projects-link"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> All Projects
              </Link>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-mono-accent text-xs tracking-[0.35em] uppercase text-neon mb-5"
              data-testid="project-division-tag"
            >
              {project.division}
            </motion.p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[0.95] max-w-4xl" data-testid="project-title">
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.3, ease: EASE }}>
                  {project.title}
                </motion.span>
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="font-mono-accent text-xs tracking-[0.2em] uppercase text-slate-300 mt-6 flex items-center gap-2"
              data-testid="project-meta"
            >
              <MapPin className="w-3.5 h-3.5 text-electric" /> {project.location} · {project.year}
            </motion.p>
          </div>
        </section>

        <section className="py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: EASE }}
              className="lg:col-span-7"
            >
              <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-6">Overview</p>
              <p className="text-base md:text-lg text-slate-300 leading-relaxed" data-testid="project-overview">{project.scope}</p>
              <div className="mt-12 space-y-5" data-testid="project-highlights">
                {project.highlights.map((h, i) => (
                  <motion.div
                    key={h}
                    initial={{ opacity: 0, x: 24 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: i * 0.1 }}
                    className="flex items-start gap-4 border-t border-white/10 pt-5"
                  >
                    <Check className="w-4 h-4 text-neon mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-300 leading-relaxed">{h}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
              className="lg:col-span-5"
            >
              <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-6">Fact Sheet</p>
              <div className="grid grid-cols-2 gap-4" data-testid="project-specs">
                {Object.entries(project.specs).map(([key, value]) => (
                  <div key={key} className="border border-white/10 bg-navy-800/40 p-5 card-glow">
                    <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-slate-500">{key}</p>
                    <p className="font-display font-bold text-sm mt-2 leading-snug">{value}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="pb-20 lg:pb-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-8">Gallery</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="project-gallery">
              {project.gallery.map((img, i) => (
                <motion.div
                  key={img + i}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.9, delay: i * 0.12, ease: EASE }}
                  className="img-zoom border border-white/10 overflow-hidden"
                >
                  <img src={img} alt={`${project.title} — view ${i + 2}`} className="w-full h-72 lg:h-96 object-cover" loading="lazy" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28 border-t border-white/10">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: EASE }}
            >
              <Quote className="w-8 h-8 text-electric mx-auto mb-8" />
              <blockquote className="font-display text-2xl md:text-3xl font-light leading-relaxed text-slate-200" data-testid="project-testimonial">
                “{project.testimonial.quote}”
              </blockquote>
              <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-neon mt-8">
                {project.testimonial.author} — {project.testimonial.org}
              </p>
              <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-slate-600 mt-3">Representative client feedback</p>
            </motion.div>
          </div>
        </section>

        <section className="py-20 border-t border-white/10 bg-navy-800/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">
              Planning something <span className="text-outline">similar?</span>
            </h2>
            <button
              onClick={() => navigate("/", { state: { scrollTo: "#contact" } })}
              className="group font-mono-accent text-xs tracking-[0.2em] uppercase bg-electric hover:bg-electric-hover text-white px-8 py-4 flex items-center gap-2 transition-colors duration-300 w-fit"
              data-testid="project-cta-button"
            >
              Discuss Your Project
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
